import { useState, useRef, useEffect, useMemo } from 'react';
import { X, Plus, Loader2, Target, Sparkles, CheckCircle2, RefreshCw, Pencil } from 'lucide-react';
import { generateItemTitle } from '../../../../lib/llmTitleService';
import { craftIdea, type CraftIdeaResult } from '../../../../lib/llmIdeaCraftingService';
import { useAiCapability } from '../../../../lib/ai/useAiCapability';
import { useNavigate } from 'react-router-dom';
import { useItemsStore } from '../../../../stores/prioritization/itemsStore';
import { useUserStore } from '../../../../stores/prioritization/userStore';
import {
  listStrategies,
  listObjectives,
  createMapping,
  type Strategy,
  type StrategicObjective,
} from '../../../../lib/strategyAlignment';
import { getCurrentWorkspaceId } from '../../../../lib/supabaseSync';
import type { ItemLanguageType, ItemMetaCategory } from '../../../../types/prioritization/lifecycle';
import type { ItemCreationProvenance } from '../../../../types/prioritization/itemProvenance';
import { useItemCategoriesStore } from '../../../../stores/prioritization/itemCategoriesStore';
import { APPROACH_KIND_LABELS } from '../../../../types/work-type-recipes';
import { useWorkTypeCatalog } from '../../../../hooks/prioritization/useWorkTypeCatalog';
import { FEATURE_FLAGS } from '../../../../lib/featureFlags';
import { getWorkspaceModules, WORKSPACE_MODULES_UPDATED_EVENT, isModuleFeatureAccessible } from '../../../../lib/workspaceModulesService';

interface CreateItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Pre-select a category when the modal opens (e.g. 'opportunity'). */
  initialCategory?: ItemLanguageType;
}

// Work type options loaded from workspace catalog (built-in + custom).

const STATIC_CATEGORY_OPTIONS: Array<{ value: ItemLanguageType; label: string; description: string }> = [
  { value: 'problem',     label: 'Problem',     description: 'Something that needs fixing' },
  { value: 'opportunity', label: 'Opportunity', description: 'Something we could gain' },
  { value: 'risk',        label: 'Risk',        description: 'Something that might go wrong' },
  { value: 'solution',    label: 'Solution',    description: 'A specific approach or implementation' },
  { value: 'commitment',  label: 'Commitment',  description: 'An external promise we must keep' },
  { value: 'investment',  label: 'Investment',  description: 'A strategic bet for future value' },
];

export function CreateItemModal({ isOpen, onClose, initialCategory }: CreateItemModalProps) {
  const navigate = useNavigate();
  const createItemWithReason = useItemsStore((s) => s.createItemWithReason);
  const userId = useUserStore((s) => s.userId);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<ItemLanguageType | ''>(initialCategory ?? '');
  const [metaCategory, setMetaCategory] = useState<ItemMetaCategory | ''>('');
  const [workType, setWorkType] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [generatingTitle, setGeneratingTitle] = useState(false);
  const titleGeneratorEnabled = useAiCapability('title_generator');

  // AI Draft mode
  type ModalMode = 'manual' | 'ai-draft';
  const [mode, setMode] = useState<ModalMode>('manual');
  const [aiInput, setAiInput] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiError, setAiError] = useState('');
  const [aiSuggestion, setAiSuggestion] = useState<CraftIdeaResult | null>(null);
  const [aiAcceptedTitle, setAiAcceptedTitle] = useState('');
  const [aiAcceptedDescription, setAiAcceptedDescription] = useState('');
  const ideaCraftingEnabled = useAiCapability('idea_crafting');
  // Snapshot of the AI-drafted title/description at the moment the user accepted
  // them (handleAiAccept). If the manual title/description still match this
  // snapshot unedited at submit time, the item is saved with creationProvenance
  // 'ai_draft'; any edit after acceptance drops it to 'human'. null = never
  // went through the AI-draft flow (plain manual entry).
  const [aiOriginSnapshot, setAiOriginSnapshot] = useState<{ title: string; description: string } | null>(null);

  // Strategy / objective linking
  const [strategies, setStrategies] = useState<Strategy[]>([]);
  const [objectives, setObjectives] = useState<StrategicObjective[]>([]);
  const [selectedStrategyId, setSelectedStrategyId] = useState('');
  const [selectedObjectiveId, setSelectedObjectiveId] = useState('');
  const [loadingStrategies, setLoadingStrategies] = useState(false);
  const [loadingObjectives, setLoadingObjectives] = useState(false);

  const titleRef = useRef<HTMLInputElement>(null);

  const workspaceId = getCurrentWorkspaceId();
  const { options: workTypeOptions } = useWorkTypeCatalog(workspaceId);
  const [workspaceFeatures, setWorkspaceFeatures] = useState<Record<string, boolean>>({});
  useEffect(() => {
    if (!workspaceId) return;
    getWorkspaceModules(workspaceId).then(({ data }) => setWorkspaceFeatures(data));
    const handler = () => { if (workspaceId) getWorkspaceModules(workspaceId).then(({ data }) => setWorkspaceFeatures(data)); };
    window.addEventListener(WORKSPACE_MODULES_UPDATED_EVENT, handler);
    return () => window.removeEventListener(WORKSPACE_MODULES_UPDATED_EVENT, handler);
  }, [workspaceId]);

  const isOpportunity = category === 'opportunity';
  const strategyModuleEnabled = useMemo(
    () => isModuleFeatureAccessible(workspaceFeatures, 'strategy', FEATURE_FLAGS.STRATEGY_NAV),
    [workspaceFeatures]
  );
  const { categories: wsCategories, fetchCategories } = useItemCategoriesStore();
  useEffect(() => {
    if (workspaceId) fetchCategories(workspaceId);
  }, [workspaceId, fetchCategories]);

  const categoryOptions = wsCategories.length > 0
    ? wsCategories.filter((c) => c.isActive).map((c) => ({
        value: c.slug as ItemLanguageType,
        label: c.label,
        description: c.description ?? '',
      }))
    : STATIC_CATEGORY_OPTIONS;

  // Load strategies once when modal opens for the first time and category is opportunity,
  // or whenever category switches to opportunity.
  useEffect(() => {
    if (!isOpen || !isOpportunity) return;
    if (strategies.length > 0) return; // already loaded
    const workspaceId = getCurrentWorkspaceId();
    if (!workspaceId) return;
    setLoadingStrategies(true);
    listStrategies(workspaceId).then((s) => {
      setStrategies(s);
      setLoadingStrategies(false);
    });
  }, [isOpen, isOpportunity, strategies.length]);

  // Load objectives when a strategy is selected
  useEffect(() => {
    if (!selectedStrategyId) {
      setObjectives([]);
      setSelectedObjectiveId('');
      return;
    }
    const workspaceId = getCurrentWorkspaceId();
    if (!workspaceId) return;
    setLoadingObjectives(true);
    setSelectedObjectiveId('');
    listObjectives(workspaceId, selectedStrategyId).then((objs) => {
      setObjectives(objs);
      setLoadingObjectives(false);
    });
  }, [selectedStrategyId]);

  useEffect(() => {
    if (isOpen) {
      setTitle('');
      setDescription('');
      setCategory(initialCategory ?? '');
      setMetaCategory('');
      setWorkType('');
      setError('');
      setSubmitting(false);
      setSelectedStrategyId('');
      setSelectedObjectiveId('');
      setObjectives([]);
      setMode('manual');
      setAiInput('');
      setAiError('');
      setAiSuggestion(null);
      setAiAcceptedTitle('');
      setAiAcceptedDescription('');
      setAiOriginSnapshot(null);
      setTimeout(() => titleRef.current?.focus(), 50);
    }
  }, [isOpen, initialCategory]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  const handleAiGenerate = async () => {
    if (!aiInput.trim() || aiGenerating) return;
    setAiGenerating(true);
    setAiError('');
    setAiSuggestion(null);
    try {
      const result = await craftIdea({ title: '', description: aiInput.trim() });
      setAiSuggestion(result);
      setAiAcceptedTitle(result.suggestedTitle);
      setAiAcceptedDescription(result.suggestedDescription);
    } catch (err) {
      setAiError(err instanceof Error ? err.message : 'Generation failed. Please try again.');
    } finally {
      setAiGenerating(false);
    }
  };

  const handleAiAccept = () => {
    setTitle(aiAcceptedTitle);
    setDescription(aiAcceptedDescription);
    // Record the accepted AI text so handleSubmit can tell whether the user
    // edited it afterward (creationProvenance: 'ai_draft' vs 'human').
    setAiOriginSnapshot({ title: aiAcceptedTitle, description: aiAcceptedDescription });
    setAiSuggestion(null);
    setAiInput('');
    setMode('manual');
    setTimeout(() => titleRef.current?.focus(), 50);
  };

  const handleGenerateTitle = async () => {
    if (!description.trim() || generatingTitle) return;
    setGeneratingTitle(true);
    try {
      const generated = await generateItemTitle({
        description,
        category: category || undefined,
      });
      setTitle(generated);
      setError('');
      titleRef.current?.focus();
    } catch {
      // fail silently — user still has the description
    } finally {
      setGeneratingTitle(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = title.trim();
    if (!trimmed) {
      setError('A title is required.');
      titleRef.current?.focus();
      return;
    }
    setSubmitting(true);
    const id = crypto.randomUUID();

    // Only an un-edited AI-drafted title/description at submit time gets
    // 'ai_draft'; any edit after handleAiAccept (or a plain manual entry
    // that never went through AI) is 'human'.
    const trimmedDescription = description.trim();
    const creationProvenance: ItemCreationProvenance | undefined = aiOriginSnapshot
      ? (trimmed === aiOriginSnapshot.title.trim() &&
         trimmedDescription === aiOriginSnapshot.description.trim()
          ? 'ai_draft'
          : 'human')
      : undefined;
    const creationSource = creationProvenance === 'ai_draft' ? 'ai' : creationProvenance === 'human' ? 'human' : undefined;
    const creationReviewStatus = creationProvenance ? 'none' : undefined;

    // Wait for the save. Going on before it finished opened a page for an item
    // that might not exist: a refused save showed "Item not found."
    const saved = await createItemWithReason({
      id,
      title: trimmed,
      description: trimmedDescription || undefined,
      itemCategory: category || undefined,
      metaCategory: metaCategory || undefined,
      work_type: workType || undefined,
      createdBy: userId ?? 'unknown',
      tags: [],
      lifecycle_stage: 'shaping', // created from item list — already accepted, skip triage
      creationProvenance,
      creationSource,
      creationReviewStatus,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any);
    if (!saved.ok) {
      setError(saved.reason ?? 'The item could not be saved.');
      setSubmitting(false);
      return;
    }

    // Link to strategy objective if one was selected
    if (selectedObjectiveId && userId) {
      try {
        await createMapping(
          {
            objectiveId: selectedObjectiveId,
            priorityId: id,
            alignmentStrength: 'primary',
            createdBy: userId,
          },
          userId
        );
      } catch (err) {
        console.warn('[CreateItemModal] Failed to create strategy mapping:', err);
      }
    }

    onClose();
    navigate(`/prioritization/items/${id}`);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-[var(--bg-raised)] shadow-2xl shadow-gray-300/40 border border-[var(--rule)]/80 overflow-hidden">
        {/* Header */}
        <div className="px-6 pt-4 pb-0 border-b border-[var(--rule)]">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2.5">
              <div className="flex items-center justify-center w-7 h-7 bg-[var(--slate-dim)]">
                <Plus className="h-3.5 w-3.5 text-[var(--text-ter)]" />
              </div>
              <h2 className="text-base font-semibold text-[var(--text-pri)]">New item</h2>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          {/* Mode toggle — only shown when idea_crafting is enabled */}
          {ideaCraftingEnabled && (
            <div className="flex gap-0 -mb-px">
              <button
                type="button"
                onClick={() => { setMode('manual'); setAiSuggestion(null); setAiError(''); }}
                className={`flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold border-b-2 transition-colors ${
                  mode === 'manual'
                    ? 'border-[var(--slate)] text-[var(--text-ter)]'
                    : 'border-transparent text-[var(--text-ter)] hover:text-[var(--text-sec)]'
                }`}
              >
                <Pencil className="h-3 w-3" />
                Manual
              </button>
              <button
                type="button"
                onClick={() => { setMode('ai-draft'); setAiSuggestion(null); setAiError(''); }}
                className={`flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold border-b-2 transition-colors ${
                  mode === 'ai-draft'
                    ? 'border-[var(--slate)] text-[var(--text-ter)]'
                    : 'border-transparent text-[var(--text-ter)] hover:text-[var(--text-sec)]'
                }`}
              >
                <Sparkles className="h-3 w-3" />
                AI Draft
              </button>
            </div>
          )}
        </div>

        {/* AI Draft panel */}
        {mode === 'ai-draft' && (
          <div className="px-6 py-5 space-y-4">
            {!aiSuggestion ? (
              <>
                <div>
                  <label className="block text-xs font-semibold text-[var(--text-sec)] mb-1.5">
                    What do you have in mind?
                  </label>
                  <p className="text-xs text-[var(--text-ter)] mb-2">
                    Describe it in your own words — raw notes, context, the problem, anything. AI will draft a title and description.
                  </p>
                  <textarea
                    autoFocus
                    value={aiInput}
                    onChange={(e) => { setAiInput(e.target.value); setAiError(''); }}
                    placeholder="e.g. Our checkout flow keeps dropping users on mobile. Support is getting tickets daily. Probably a JS error on the payment step but we haven't dug in yet…"
                    rows={6}
                    className="w-full px-3 py-2.5 text-sm border border-[var(--rule)] resize-none focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 outline-none transition-all bg-[var(--bg-raised)] leading-relaxed"
                  />
                  {aiError && (
                    <p className="mt-1.5 text-xs text-[var(--coral)]">{aiError}</p>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => setMode('manual')}
                    className="text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)] transition-colors"
                  >
                    Enter manually instead
                  </button>
                  <button
                    type="button"
                    onClick={handleAiGenerate}
                    disabled={!aiInput.trim() || aiGenerating}
                    className="flex items-center gap-1.5 px-4 py-1.5 bg-[var(--slate)] text-white text-sm font-semibold hover:bg-[var(--slate)] disabled:opacity-50 disabled:cursor-not-allowed shadow-sm shadow-indigo-200 transition-all"
                  >
                    {aiGenerating ? (
                      <span className="flex items-center gap-1.5">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        Generating…
                      </span>
                    ) : (
                      <span className="flex items-center gap-1.5">
                        <Sparkles className="h-3.5 w-3.5" />
                        Generate
                      </span>
                    )}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div>
                  <p className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide mb-3">AI suggestion — review and edit before accepting</p>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-semibold text-[var(--text-sec)] mb-1">Title</label>
                      <input
                        type="text"
                        value={aiAcceptedTitle}
                        onChange={(e) => setAiAcceptedTitle(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-[var(--slate)] focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 outline-none transition-all bg-[var(--slate-dim)]/30"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-[var(--text-sec)] mb-1">Description</label>
                      <textarea
                        value={aiAcceptedDescription}
                        onChange={(e) => setAiAcceptedDescription(e.target.value)}
                        rows={4}
                        className="w-full px-3 py-2 text-sm border border-[var(--slate)] resize-none focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 outline-none transition-all bg-[var(--slate-dim)]/30 leading-relaxed"
                      />
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <button
                    type="button"
                    onClick={() => { setAiSuggestion(null); setAiError(''); }}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[var(--text-sec)] border border-[var(--rule)] hover:bg-[var(--surface)] transition-colors"
                  >
                    <RefreshCw className="h-3.5 w-3.5" />
                    Try again
                  </button>
                  <button
                    type="button"
                    onClick={handleAiAccept}
                    disabled={!aiAcceptedTitle.trim()}
                    className="flex items-center gap-1.5 px-4 py-1.5 bg-[var(--slate)] text-white text-sm font-semibold hover:bg-[var(--slate)] disabled:opacity-50 disabled:cursor-not-allowed shadow-sm shadow-indigo-200 transition-all"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    Accept &amp; continue
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className={`px-6 py-5 space-y-4 ${mode === 'ai-draft' ? 'hidden' : ''}`}>
          {/* Title */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-semibold text-[var(--text-sec)]">
                Title <span className="text-[var(--coral)]">*</span>
              </label>
              {description.trim() && titleGeneratorEnabled && (
                <button
                  type="button"
                  onClick={handleGenerateTitle}
                  disabled={generatingTitle}
                  title="Generate title from description"
                  className="flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-[var(--text-sec)] border border-[var(--slate)] dark:border-[var(--slate)]/60 bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40 hover:bg-[var(--slate-dim)] dark:hover:bg-[var(--slate)]/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {generatingTitle
                    ? <Loader2 className="h-3 w-3 animate-spin" />
                    : <Sparkles className="h-3 w-3" />}
                  {generatingTitle ? 'Generating…' : 'Generate title'}
                </button>
              )}
            </div>
            <input
              ref={titleRef}
              type="text"
              value={title}
              onChange={(e) => { setTitle(e.target.value); setError(''); }}
              placeholder="What needs attention?"
              className={`w-full px-3 py-2 text-sm border  outline-none transition-all
                ${error
                  ? 'border-[var(--coral)] ring-2 ring-red-100 bg-[var(--coral-dim)]/30'
                  : 'border-[var(--rule)] focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 bg-[var(--bg-raised)]'
                }`}
            />
            {error && <p className="mt-1.5 text-xs text-[var(--coral)]">{error}</p>}
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-semibold text-[var(--text-sec)] mb-1.5">
              Description <span className="text-[var(--text-ter)] font-normal">(optional)</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief context or details…"
              rows={3}
              className="w-full px-3 py-2 text-sm border border-[var(--rule)] resize-none focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 outline-none transition-all bg-[var(--bg-raised)]"
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-xs font-semibold text-[var(--text-sec)] mb-1.5">
              Category <span className="text-[var(--text-ter)] font-normal">(optional)</span>
            </label>
            <select
              value={category}
              onChange={(e) => {
                setCategory(e.target.value as ItemLanguageType | '');
                setSelectedStrategyId('');
                setSelectedObjectiveId('');
                setObjectives([]);
              }}
              className="w-full px-3 py-2 text-sm border border-[var(--rule)] focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 outline-none transition-all bg-[var(--bg-raised)] text-[var(--text-sec)]"
            >
              <option value="">— Select a category —</option>
              {categoryOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            {category && (
              <p className="mt-1 text-xs text-[var(--text-ter)]">
                {categoryOptions.find((o) => o.value === category)?.description}
              </p>
            )}
          </div>

          {/* Meta-category — what kind of thing is this? */}
          <div>
            <label className="block text-xs font-semibold text-[var(--text-sec)] mb-1.5">
              What kind of thing is this?{' '}
              <span className="text-[var(--text-ter)] font-normal">(optional — helps filter for sessions)</span>
            </label>
            <div className="grid grid-cols-3 gap-1.5">
              {([
                { value: 'people',      label: 'People' },
                { value: 'places',      label: 'Places' },
                { value: 'things',      label: 'Things' },
                { value: 'activities',  label: 'Activities' },
                { value: 'information', label: 'Information' },
                { value: 'time',        label: 'Time' },
              ] as { value: ItemMetaCategory; label: string }[]).map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setMetaCategory(prev => prev === opt.value ? '' : opt.value)}
                  className={` border px-2 py-1.5 text-xs font-medium transition-colors ${
                    metaCategory === opt.value
                      ? 'border-[var(--slate)] bg-[var(--slate-dim)] text-[var(--text-sec)]'
                      : 'border-[var(--rule)] text-[var(--text-sec)] hover:border-[var(--rule)] hover:bg-[var(--surface)]'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Work type — how will this be executed? (Canon Doc 21) */}
          <div>
            <label className="block text-xs font-semibold text-[var(--text-sec)] mb-1.5">
              How would this be executed?{' '}
              <span className="text-[var(--text-ter)] font-normal">(optional — tells planning how this kind of work is approached)</span>
            </label>
            <select
              value={workType}
              onChange={(e) => setWorkType(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-[var(--rule)] focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 outline-none transition-all bg-[var(--bg-raised)] text-[var(--text-sec)]"
            >
              <option value="">— Decide later —</option>
              {workTypeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}{!opt.isBuiltin ? ' (custom)' : ''} · {APPROACH_KIND_LABELS[opt.approachKind]} · ×{opt.multiplier}
                </option>
              ))}
            </select>
            {workType && (() => {
              const selected = workTypeOptions.find((o) => o.value === workType);
              if (!selected) return null;
              return (
                <p className="mt-1 text-xs text-[var(--text-ter)]">
                  Approach: {APPROACH_KIND_LABELS[selected.approachKind]}.
                  Drives Planning&apos;s uncertainty and required artifacts.
                  {selected.description ? ` ${selected.description}` : ''}
                </p>
              );
            })()}
          </div>

          {/* Strategy connection — only shown for opportunities when the Strategy module is enabled */}
          {isOpportunity && strategyModuleEnabled && (
            <div className="space-y-3 pt-1 border-t border-[var(--rule)]">
              <div className="flex items-center gap-1.5">
                <Target className="w-3.5 h-3.5 text-[var(--green)]" />
                <span className="text-xs font-semibold text-[var(--text-sec)]">
                  Strategy alignment <span className="text-[var(--text-ter)] font-normal">(optional)</span>
                </span>
              </div>

              {/* Strategy selector */}
              <div>
                <label className="block text-xs text-[var(--text-ter)] mb-1">Strategy / Outcome</label>
                <select
                  value={selectedStrategyId}
                  onChange={(e) => setSelectedStrategyId(e.target.value)}
                  disabled={loadingStrategies}
                  className="w-full px-3 py-2 text-sm border border-[var(--rule)] focus:border-[var(--green)] focus:ring-2 focus:ring-emerald-100 outline-none transition-all bg-[var(--bg-raised)] text-[var(--text-sec)] disabled:opacity-60"
                >
                  <option value="">
                    {loadingStrategies ? 'Loading…' : strategies.length === 0 ? 'No strategies yet' : '— Select a strategy —'}
                  </option>
                  {strategies.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>

              {/* Objective selector — only shown when a strategy is selected */}
              {selectedStrategyId && (
                <div>
                  <label className="block text-xs text-[var(--text-ter)] mb-1">Objective</label>
                  <select
                    value={selectedObjectiveId}
                    onChange={(e) => setSelectedObjectiveId(e.target.value)}
                    disabled={loadingObjectives}
                    className="w-full px-3 py-2 text-sm border border-[var(--rule)] focus:border-[var(--green)] focus:ring-2 focus:ring-emerald-100 outline-none transition-all bg-[var(--bg-raised)] text-[var(--text-sec)] disabled:opacity-60"
                  >
                    <option value="">
                      {loadingObjectives ? 'Loading…' : objectives.length === 0 ? 'No objectives for this strategy' : '— Select an objective —'}
                    </option>
                    {objectives.map((o) => (
                      <option key={o.id} value={o.id}>{o.name}</option>
                    ))}
                  </select>
                  {selectedObjectiveId && (
                    <p className="mt-1 text-xs text-[var(--text-ter)]">
                      This opportunity will be mapped to the selected objective.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="px-3.5 py-1.5 text-sm font-medium text-[var(--text-sec)] hover:text-[var(--text-pri)] hover:bg-[var(--hover-tint)] transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !title.trim()}
              className="flex items-center gap-1.5 px-4 py-1.5 bg-[var(--slate)] text-white text-sm font-semibold hover:bg-[var(--slate)] disabled:opacity-50 disabled:cursor-not-allowed shadow-sm shadow-indigo-200 transition-all"
            >
              {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
              Create Item
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
